import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { GoogleVoiceOption } from '../types';
import { buildGoogleVoicePreviewUrl } from '../utils/voicePreview';

interface GoogleVoicePreviewButtonProps {
  languageCode?: string;
  voice?: GoogleVoiceOption;
  disabled?: boolean;
}

export default function GoogleVoicePreviewButton({ languageCode = '', voice, disabled = false }: GoogleVoicePreviewButtonProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isUnavailable, setIsUnavailable] = useState(false);
  const voiceId = voice?.voiceId ?? '';

  const previewUrl = useMemo(() => buildGoogleVoicePreviewUrl(languageCode, voiceId), [languageCode, voiceId]);

  const stopPlayback = useCallback((audio: HTMLAudioElement | null = audioRef.current) => {
    if (!audio) return;
    audio.pause();
    audio.currentTime = 0;
    setIsPlaying(false);
  }, []);

  useEffect(() => {
    stopPlayback();
  }, [languageCode, voiceId, stopPlayback]);

  useEffect(() => {
    stopPlayback(audioRef.current);
    setIsUnavailable(false);

    if (!previewUrl) {
      audioRef.current = null;
      return;
    }

    const audio = new Audio(previewUrl);
    audio.preload = 'metadata';

    const handleEnded = () => {
      setIsPlaying(false);
      audio.currentTime = 0;
    };

    const handleError = () => {
      audio.pause();
      audio.currentTime = 0;
      setIsPlaying(false);
      setIsUnavailable(true);
    };

    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);
    audioRef.current = audio;

    return () => {
      audio.pause();
      audio.currentTime = 0;
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      if (audioRef.current === audio) {
        audioRef.current = null;
      }
    };
  }, [previewUrl, stopPlayback]);

  useEffect(() => {
    if (!disabled) return;
    stopPlayback();
  }, [disabled, stopPlayback]);

  const handleToggle = async () => {
    const audio = audioRef.current;
    if (!audio || disabled || isUnavailable) return;

    if (isPlaying) {
      stopPlayback(audio);
      return;
    }

    try {
      audio.currentTime = 0;
      await audio.play();
      setIsPlaying(true);
    } catch {
      stopPlayback(audio);
      setIsUnavailable(true);
    }
  };

  const isDisabled = disabled || isUnavailable || !previewUrl;
  const ariaLabel = voice?.displayName ? `Preview ${voice.displayName} sample` : 'Preview voice sample';

  return (
    <Tooltip title={isUnavailable ? 'Preview unavailable' : isPlaying ? 'Stop preview' : 'Play preview'}>
      <span>
        <IconButton
          aria-label={ariaLabel}
          data-testid='google-voice-preview-button'
          size='small'
          disabled={isDisabled}
          color={isPlaying ? 'primary' : 'default'}
          onClick={() => {
            void handleToggle();
          }}
          onMouseDown={(event) => {
            event.preventDefault();
          }}
        >
          <VolumeUpIcon fontSize='small' />
        </IconButton>
      </span>
    </Tooltip>
  );
}
